'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { I18N_COOKIE_NAME } from '~/lib/i18n/i18n.settings';
import {
  ARABIC_PREFIX,
  isBilingualMarketingPath,
  localizedHref,
  localizedMarketingPath,
  stripArabicPrefix,
} from '~/lib/marketing/routes';
import { asMarketingLocale } from '~/lib/marketing/site';
import { AskFlowLogo } from '~/components/askflow-logo';

import { MarketingHeaderAccountSection } from './header-account-section';
import { SolutionsMenu, SolutionsMobileList } from './solutions-menu';

const HEADER_NAV = {
  platform: { en: 'Platform', ar: 'المنصة' },
  builtWithYou: { en: 'Built with you', ar: 'نبنيها معك' },
} as const;

/*
 * Palette note: hex values below are the askflow-brand.css primitives
 * (--ask-sand #ede8e2, --ask-ink #202020). Tailwind arbitrary values are used
 * because the opacity-modifier syntax doesn't compose with CSS vars here.
 *
 * Navigation is landing-site only: no links into the booking deployment.
 * Booking/demo intent goes through the Google Calendar scheduling modal in
 * the account section, and the contact form at /#contact.
 */

interface MarketingHeaderProps {
  /**
   * Accepted (and ignored) so the component satisfies the tenant
   * layout-registry's LandingPageHeaderComponent contract; the marketing
   * header always renders the Flow logo, never a tenant logo.
   */
  logoUrl?: string | null;
}

export function MarketingHeader(_props: MarketingHeaderProps) {
  const pathname = usePathname();
  const { t, i18n } = useTranslation('landing');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isRTL = i18n.language === 'ar';

  const locale = asMarketingLocale(i18n.language);

  const navLinks = [
    { label: HEADER_NAV.platform[locale], href: '/#platform' },
    { label: HEADER_NAV.builtWithYou[locale], href: '/#built-with-you' },
    { label: t('nav.customers', { defaultValue: 'Customers' }), href: '/customers' },
    { label: t('nav.faq', { defaultValue: 'FAQ' }), href: '/faq' },
    { label: t('nav.contact', { defaultValue: 'Contact' }), href: '/#contact' },
  ].map((link) => ({ ...link, href: localizedHref(link.href, locale) }));

  // Transparent-over-hero is a landing-page-only treatment: '/' (and legacy
  // '/flow') render the full-bleed ink hero directly beneath the fixed header.
  // Every other route shows the header spacer instead, so the header must be
  // solid immediately. Deriving this from !isLandingPage (rather than a route
  // allow-list) means new marketing routes can never regress to an invisible
  // white-on-white header.
  const isLandingPage =
    pathname === '/' || pathname === '/flow' || pathname === ARABIC_PREFIX;
  const shouldBeSolid = !isLandingPage;

  // Bilingual marketing pages switch URL (/faq <-> /ar/faq) so each language
  // keeps its own indexable address; other routes just flip the cookie.
  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    document.cookie = `${I18N_COOKIE_NAME}=${newLang};path=/;max-age=31536000`;

    const plainPath = stripArabicPrefix(window.location.pathname);

    if (isBilingualMarketingPath(plainPath)) {
      window.location.assign(
        `${localizedMarketingPath(plainPath, newLang)}${window.location.search}${window.location.hash}`,
      );
      return;
    }

    window.location.reload();
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const solid = shouldBeSolid || scrolled;

  return (
    <>
      <header
        dir={isRTL ? 'rtl' : 'ltr'}
        // Close the mobile drawer once keyboard focus leaves the header, so
        // focus never lands on page content hidden beneath the open panel.
        onBlur={(event) => {
          if (
            mobileMenuOpen &&
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setMobileMenuOpen(false);
          }
        }}
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          mobileMenuOpen
            ? 'bg-[#ede8e2] pt-3 pb-1'
            : solid
              ? 'bg-[#ede8e2]/95 py-3 backdrop-blur-sm'
              : 'bg-transparent py-5',
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-8">
          {/* Logo */}
          <Link href={localizedHref('/', locale)} className="relative z-10 flex-shrink-0 block">
            <AskFlowLogo
              className={cn(
                'h-8 md:h-9 transition-colors duration-300',
                solid || mobileMenuOpen ? 'text-[#202020]' : 'text-[#f7f6f4]',
              )}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            <SolutionsMenu locale={locale} solid={solid} />
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  'transition-colors duration-300 text-sm tracking-[1px] font-roboto-mono uppercase whitespace-nowrap',
                  solid
                    ? 'text-[#202020]/80 hover:text-[#202020]'
                    : 'text-white/90 hover:text-white',
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <button
              onClick={toggleLanguage}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-roboto-mono uppercase tracking-wider transition-colors duration-200 border',
                solid
                  ? 'border-[#202020]/30 text-[#202020]/80 hover:bg-[#202020]/10'
                  : 'border-white/30 text-white/90 hover:bg-white/10',
              )}
              aria-label="Switch language"
            >
              <Globe className="w-3.5 h-3.5" />
              {i18n.language === 'ar' ? 'EN' : 'AR'}
            </button>
            <MarketingHeaderAccountSection isSolid={solid} />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={toggleLanguage}
              className={cn(
                'p-2 transition-colors',
                solid || mobileMenuOpen ? 'text-[#202020]' : 'text-white',
              )}
              aria-label="Switch language"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={cn(
                'p-2 -mr-2 transition-colors',
                solid || mobileMenuOpen ? 'text-[#202020]' : 'text-white',
              )}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 top-[56px] z-40 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        {/* Mobile Menu */}
        <div
          className={cn(
            'lg:hidden absolute top-full left-0 right-0 bg-[#ede8e2] transition-all duration-300 overflow-hidden z-50',
            mobileMenuOpen ? 'max-h-[calc(100dvh-56px)] overflow-y-auto' : 'max-h-0',
          )}
        >
          <nav className="flex flex-col pb-4 px-6">
            <SolutionsMobileList
              locale={locale}
              onNavigate={() => setMobileMenuOpen(false)}
            />
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#202020]/90 hover:text-[#202020] transition-colors duration-300 text-base tracking-[1px] font-roboto-mono py-3 border-b border-[#202020]/10"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4">
              <MarketingHeaderAccountSection
                onNavigate={() => setMobileMenuOpen(false)}
                isSolid={true}
              />
            </div>
          </nav>
        </div>
      </header>
      {/* Spacer for fixed header - only on non-landing pages */}
      {!isLandingPage && <div className="h-[64px]" />}
    </>
  );
}
